import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type PeerContact = {
  /** id de l'autre élève (peer) */
  id: string;
  display_name: string;
  avatar_url: string | null;
  /** id de la ligne peer_links (utile pour révoquer) */
  linkId: string;
  /** true si c'est moi qui ai initié l'invitation */
  iAmInviter: boolean;
};

type PeerLinkRow = {
  id: string;
  inviter_id: string;
  invitee_id: string;
  status: 'pending' | 'active' | 'revoked';
  inviter: { id: string; display_name: string; avatar_url: string | null } | null;
  invitee: { id: string; display_name: string; avatar_url: string | null } | null;
};

/** Liste les camarades (élève-élève) liés à `studentId`. */
export function useMyPeers(studentId: string) {
  return useQuery({
    queryKey: ['my-peers', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<PeerContact[]> => {
      const { data, error } = await supabase
        .from('peer_links')
        .select(
          `id, inviter_id, invitee_id, status,
           inviter:profiles!peer_links_inviter_id_fkey ( id, display_name:visible_name, avatar_url ),
           invitee:profiles!peer_links_invitee_id_fkey ( id, display_name:visible_name, avatar_url )`,
        )
        .or(`inviter_id.eq.${studentId},invitee_id.eq.${studentId}`)
        .eq('status', 'active')
        .order('updated_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as PeerLinkRow[])
        .map<PeerContact | null>((row) => {
          const iAmInviter = row.inviter_id === studentId;
          const other = iAmInviter ? row.invitee : row.inviter;
          if (!other) return null;
          return {
            id: other.id,
            display_name: other.display_name,
            avatar_url: other.avatar_url,
            linkId: row.id,
            iAmInviter,
          };
        })
        .filter((c): c is PeerContact => c !== null);
    },
  });
}

/** Récupère (ou crée) le code d'invitation peer permanent de l'élève courant. */
export function useEnsurePeerInviteCode(studentId: string) {
  return useQuery({
    queryKey: ['peer-invite-code', studentId],
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string> => {
      const { data, error } = await supabase.rpc('ensure_peer_invite_code');
      if (error) throw new Error(error.message);
      return data as string;
    },
  });
}

/** Accepte une invitation peer via le code partagé. */
export function useAcceptPeerInvite(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<{ peer_id: string; peer_display_name: string }> => {
      const { data, error } = await supabase.rpc('accept_peer_invite', { p_code: code.trim() });
      if (error) throw new Error(error.message);
      const row = (data as Array<{ peer_id: string; peer_display_name: string }>)?.[0];
      if (!row) throw new Error('Réponse inattendue du serveur.');
      return row;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-peers', studentId] });
    },
  });
}

/** Révoque un lien peer existant. */
export function useRevokePeerLink(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (peerId: string) => {
      const { error } = await supabase.rpc('revoke_peer_link', { p_peer_id: peerId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-peers', studentId] });
    },
  });
}

/** Demande de camarade reçue (en attente), créée p. ex. en relevant un défi (0299). */
export type PeerRequest = {
  linkId: string;
  id: string;
  display_name: string;
  avatar_url: string | null;
};

/** Demandes de camarade REÇUES par `studentId` (lui seul peut accepter). */
export function usePeerRequests(studentId: string) {
  return useQuery({
    queryKey: ['peer-requests', studentId],
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<PeerRequest[]> => {
      const { data, error } = await supabase
        .from('peer_links')
        .select(
          `id, inviter:profiles!peer_links_inviter_id_fkey ( id, display_name:visible_name, avatar_url )`,
        )
        .eq('invitee_id', studentId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      type Row = {
        id: string;
        inviter: { id: string; display_name: string; avatar_url: string | null } | null;
      };
      return ((data ?? []) as unknown as Row[])
        .filter((r) => r.inviter)
        .map((r) => ({
          linkId: r.id,
          id: r.inviter!.id,
          display_name: r.inviter!.display_name,
          avatar_url: r.inviter!.avatar_url,
        }));
    },
  });
}

/** Accepte ou refuse une demande de camarade (RPC `respond_peer_request`, 0299). */
export function useRespondPeerRequest(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ linkId, accept }: { linkId: string; accept: boolean }) => {
      const { error } = await supabase.rpc(
        'respond_peer_request' as never,
        { p_link_id: linkId, p_accept: accept } as never,
      );
      if (error) {
        throw new Error(
          error.message.includes('peer_request_not_found')
            ? 'Cette demande n’est plus en attente.'
            : error.message,
        );
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['peer-requests', studentId] });
      qc.invalidateQueries({ queryKey: ['my-peers', studentId] });
    },
  });
}
